import * as React from 'react'
import { ArrowLeft, ChevronDown, HandHeart, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { getJSON, patchJSON } from '@/lib/api'
import {
  AccountingMappingSelect,
  type AccountingProviderReference,
} from '@/pages/AccountingMappingSelect'

interface CampaignDetail {
  id: number
  name: string
  goal: string | null
  raised: string
  progress_percent: number | null
  gift_count: number
  donor_count: number
  revenue_destination: AccountingProviderReference | null
}

export function CampaignDetailPage({
  campaignId,
  onBack,
}: {
  campaignId: number
  onBack: () => void
}) {
  const campaign = React.useMemo(
    () => getJSON<{ campaign: CampaignDetail }>(`/api/v1/campaigns/${campaignId}`),
    [campaignId],
  )
  const [state, setState] = React.useState<{ data?: CampaignDetail; error?: boolean }>({})
  const [accounting, setAccounting] = React.useState<{
    connected: boolean
    destinations: AccountingProviderReference[]
  }>({ connected: false, destinations: [] })
  const [savingDestination, setSavingDestination] = React.useState(false)

  React.useEffect(() => {
    let active = true
    campaign
      .then(({ campaign: detail }) => active && setState({ data: detail }))
      .catch(() => active && setState({ error: true }))
    return () => {
      active = false
    }
  }, [campaign])

  React.useEffect(() => {
    let active = true
    getJSON<{ accounting_settings: { connection: { status: string } | null } }>(
      '/api/v1/accounting_settings',
    )
      .then(({ accounting_settings }) => {
        if (accounting_settings.connection?.status !== 'connected') return
        return getJSON<{
          revenue_destination_mappings: { destinations: AccountingProviderReference[] }
        }>('/api/v1/revenue_destination_mappings')
      })
      .then((payload) => {
        if (active && payload)
          setAccounting({
            connected: true,
            destinations: payload.revenue_destination_mappings.destinations,
          })
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  if (state.error) return <p className="p-6 text-body-sm text-destructive">Campaign unavailable.</p>
  if (!state.data)
    return <p className="p-6 text-body-sm text-muted-foreground">Loading Campaign…</p>

  const detail = state.data
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mb-3 -ml-2 text-muted-foreground"
        onClick={onBack}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Campaigns
      </Button>
      <h1 className="text-subheading font-semibold">{detail.name}</h1>
      {accounting.connected && (
        <CampaignAccountingDisclosure
          detail={detail}
          destinations={accounting.destinations}
          saving={savingDestination}
          onSave={(destination) => {
            setSavingDestination(true)
            patchCampaignDestination(detail.id, destination)
              .then((updated) => setState({ data: { ...detail, revenue_destination: updated } }))
              .finally(() => setSavingDestination(false))
          }}
        />
      )}
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <Metric icon={HandHeart} label="Raised" value={detail.raised}>
          {detail.goal && detail.progress_percent !== null && (
            <>
              <p className="text-caption text-muted-foreground">
                of {detail.goal} goal · {detail.progress_percent}%
              </p>
              <Progress
                value={Math.min(detail.progress_percent, 100)}
                className="mt-2"
                aria-label={`${detail.progress_percent}% progress`}
              />
            </>
          )}
        </Metric>
        <Metric icon={HandHeart} label="Gifts" value={String(detail.gift_count)} />
        <Metric icon={Users} label="Donors" value={String(detail.donor_count)} />
      </div>
    </div>
  )
}

function patchCampaignDestination(
  campaignId: number,
  destinationId: string | null,
): Promise<AccountingProviderReference | null> {
  return patchJSON<{
    revenue_destination_mappings: {
      campaigns: Array<{ id: number; destination: AccountingProviderReference | null }>
    }
  }>('/api/v1/revenue_destination_mappings', {
    revenue_destination_mappings: {
      campaign_id: campaignId,
      campaign_destination_id: destinationId,
    },
  }).then(({ revenue_destination_mappings }) => {
    const row = revenue_destination_mappings.campaigns.find(
      (campaign) => campaign.id === campaignId,
    )
    return row?.destination ?? null
  })
}

function CampaignAccountingDisclosure({
  detail,
  destinations,
  saving,
  onSave,
}: {
  detail: CampaignDetail
  destinations: AccountingProviderReference[]
  saving: boolean
  onSave: (destinationId: string | null) => void
}) {
  return (
    <Collapsible className="group/accounting mt-4 rounded-md border border-border bg-card px-4 py-3">
      <CollapsibleTrigger className="flex w-full items-center text-left text-body-sm font-medium">
        <span>Accounting</span>
        <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]/accounting:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 max-w-xl space-y-1">
          <label
            htmlFor="campaign-revenue-destination"
            className="text-caption text-muted-foreground"
          >
            Revenue Destination
          </label>
          <AccountingMappingSelect
            id="campaign-revenue-destination"
            destinations={destinations}
            value={detail.revenue_destination?.id}
            disabled={saving}
            onChange={onSave}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function Metric({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: typeof HandHeart
  label: string
  value: string
  children?: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded bg-primary/10 text-primary">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="text-caption text-muted-foreground">{label}</span>
        </div>
        <p className="mt-3 text-subheading font-semibold tabular-nums">{value}</p>
        {children}
      </CardContent>
    </Card>
  )
}
